/**
 * PWA Service
 * Handles PWA install prompts, service worker updates, and offline detection
 */
(function () {
    'use strict';

    angular
        .module('app.core')
        .factory('pwaSvc', pwaSvc);

    pwaSvc.$inject = ['$rootScope', '$timeout', '$window', 'storageSvc'];

    function pwaSvc($rootScope, $timeout, $window, storageSvc) {
        var service = {
            // Properties
            isInstalled: isInstalled,
            isOnline: navigator.onLine,
            canInstall: false,
            updateAvailable: false,

            // Methods
            promptInstall: promptInstall,
            dismissInstall: dismissInstall,
            updateServiceWorker: updateServiceWorker,
            clearCache: clearCache,
            getVersion: getVersion,
            registerBackgroundSync: registerBackgroundSync
        };

        var deferredPrompt = null;
        var registration = null;
        var installDismissed = storageSvc.get('pwa-install-dismissed', true);  // Default to dismissed (prefer native apps)

        // Multi-language translations for PWA notifications
        var translations = {
            'tr': {
                updateReady: 'Güncelleme Hazır',
                newVersionAvailable: 'Yeni bir sürüm mevcut',
                updateButton: 'Güncelle',
                laterButton: 'Sonra',
                backOnline: 'Çevrimiçi',
                goneOffline: 'Çevrimdışı',
                installPrompt: 'Endeksa\'yı Yükle',
                installDescription: 'Ana ekranınıza hızlı erişim ekleyin'
            },
            'en': {
                updateReady: 'Update Ready',
                newVersionAvailable: 'A new version is available',
                updateButton: 'Update',
                laterButton: 'Later',
                backOnline: 'Online',
                goneOffline: 'Offline',
                installPrompt: 'Install Endeksa',
                installDescription: 'Add quick access to your home screen'
            },
            'es-ES': {
                updateReady: 'Actualización Lista',
                newVersionAvailable: 'Una nueva versión está disponible',
                updateButton: 'Actualizar',
                laterButton: 'Más tarde',
                backOnline: 'En línea',
                goneOffline: 'Sin conexión',
                installPrompt: 'Instalar Endeksa',
                installDescription: 'Agregar acceso rápido a tu pantalla de inicio'
            },
            'pt-PT': {
                updateReady: 'Atualização Pronta',
                newVersionAvailable: 'Uma nova versão está disponível',
                updateButton: 'Atualizar',
                laterButton: 'Mais tarde',
                backOnline: 'Online',
                goneOffline: 'Offline',
                installPrompt: 'Instalar Endeksa',
                installDescription: 'Adicionar acesso rápido ao ecrã inicial'
            }
        };

        init();

        return service;

        // ====================================================================
        // LANGUAGE HELPERS
        // ====================================================================

        function getCurrentLanguage() {
            // Priority 1: Check $rootScope.lang (set by app)
            if ($rootScope.lang) {
                return $rootScope.lang;
            }

            // Priority 2: Check localStorage
            var storedLang = localStorage.getItem('endeksaLanguage') || localStorage.getItem('language');
            if (storedLang && translations[storedLang]) {
                return storedLang;
            }

            // Priority 3: Check URL path
            var path = $window.location.pathname;
            var urlLangMatch = path.match(/^\/(tr|en|es-ES|pt-PT)\//);
            if (urlLangMatch) {
                return urlLangMatch[1];
            }

            // Priority 4: Browser language
            var browserLang = navigator.language || navigator.userLanguage;
            if (browserLang) {
                if (translations[browserLang]) {
                    return browserLang;
                }
                var baseLang = browserLang.split('-')[0];
                if (baseLang === 'es') return 'es-ES';
                if (baseLang === 'pt') return 'pt-PT';
                if (baseLang === 'en') return 'en';
            }

            // Default: Turkish
            return 'tr';
        }

        function getTranslation(key) {
            var lang = getCurrentLanguage();
            return translations[lang][key] || translations['tr'][key] || key;
        }

        // ====================================================================
        // INITIALIZATION
        // ====================================================================

        function init() {
            // Listen for install prompt
            $window.addEventListener('beforeinstallprompt', handleInstallPrompt);

            // Listen for app installed
            $window.addEventListener('appinstalled', handleAppInstalled);

            // Listen for online/offline events
            $window.addEventListener('online', handleOnline);
            $window.addEventListener('offline', handleOffline);

            // Check for Service Worker updates
            if ('serviceWorker' in navigator) {
                navigator.serviceWorker.ready.then(function (reg) {
                    registration = reg;

                    // Check for updates every hour
                    setInterval(function () {
                        reg.update();
                    }, 60 * 60 * 1000);

                    // Listen for updates
                    reg.addEventListener('updatefound', handleUpdateFound);
                });

                // Listen for messages from SW
                navigator.serviceWorker.addEventListener('message', handleSWMessage);
            }

            console.log('[PWA] Service initialized');
        }

        // ====================================================================
        // INSTALL PROMPT
        // ====================================================================

        function handleInstallPrompt(e) {
            console.log('[PWA] Install prompt available');

            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();

            // Store the event for later use
            deferredPrompt = e;
            service.canInstall = true;

            // Don't auto-show install banner (prefer native apps)
            // Users can still manually install via browser menu or custom trigger
            // if (!installDismissed) {
            //     $timeout(function () {
            //         showInstallBanner();
            //     }, 3000);
            // }

            $rootScope.$broadcast('pwa:install-available');
        }

        function promptInstall() {
            if (!deferredPrompt) {
                console.log('[PWA] No install prompt available');
                return Promise.reject('No install prompt available');
            }

            // Show the install prompt
            deferredPrompt.prompt();

            // Wait for the user to respond
            return deferredPrompt.userChoice.then(function (choiceResult) {
                console.log('[PWA] User choice:', choiceResult.outcome);

                if (choiceResult.outcome === 'accepted') {
                    console.log('[PWA] User accepted install');
                    hideInstallBanner();
                } else {
                    console.log('[PWA] User dismissed install');
                }

                // Clear the deferred prompt
                deferredPrompt = null;
                service.canInstall = false;

                return choiceResult;
            });
        }

        function dismissInstall() {
            installDismissed = true;
            storageSvc.set('pwa-install-dismissed', true);
            hideInstallBanner();
            $rootScope.$broadcast('pwa:install-dismissed');
        }

        function handleAppInstalled() {
            console.log('[PWA] App installed successfully');
            hideInstallBanner();
            deferredPrompt = null;
            service.canInstall = false;
            $rootScope.$broadcast('pwa:installed');

            // Track installation
            if ($window.gtag) {
                $window.gtag('event', 'pwa_installed', {
                    event_category: 'PWA',
                    event_label: 'App Installed'
                });
            }
        }

        function isInstalled() {
            // Check if running in standalone mode
            return ($window.matchMedia('(display-mode: standalone)').matches) ||
                ($window.navigator.standalone) ||
                document.referrer.includes('android-app://');
        }

        // ====================================================================
        // SERVICE WORKER UPDATES
        // ====================================================================

        function handleUpdateFound() {
            console.log('[PWA] Service Worker update found');

            var newWorker = registration.installing;

            newWorker.addEventListener('statechange', function () {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                    console.log('[PWA] New Service Worker ready');
                    service.updateAvailable = true;

                    $timeout(function () {
                        showUpdateNotification();
                        $rootScope.$broadcast('pwa:update-available');
                    });
                }
            });
        }

        function updateServiceWorker() {
            if (!registration || !registration.waiting) {
                console.log('[PWA] No waiting Service Worker');
                return Promise.reject('No waiting Service Worker');
            }

            // Tell the waiting SW to skip waiting and activate
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });

            // Reload the page when the new SW is activated
            navigator.serviceWorker.addEventListener('controllerchange', function () {
                console.log('[PWA] New Service Worker activated, reloading...');
                $window.location.reload();
            });

            return Promise.resolve();
        }

        function clearCache() {
            if (!navigator.serviceWorker.controller) {
                console.log('[PWA] No Service Worker controller');
                return Promise.reject('No Service Worker');
            }

            navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });

            return new Promise(function (resolve) {
                navigator.serviceWorker.addEventListener('message', function handler(event) {
                    if (event.data && event.data.type === 'CACHE_CLEARED') {
                        console.log('[PWA] Cache cleared');
                        navigator.serviceWorker.removeEventListener('message', handler);
                        resolve();
                    }
                });
            });
        }

        function getVersion() {
            if (!navigator.serviceWorker.controller) {
                return Promise.reject('No Service Worker');
            }

            var messageChannel = new MessageChannel();

            return new Promise(function (resolve, reject) {
                messageChannel.port1.onmessage = function (event) {
                    if (event.data && event.data.version) {
                        resolve(event.data.version);
                    } else {
                        reject('No version returned');
                    }
                };

                navigator.serviceWorker.controller.postMessage(
                    { type: 'GET_VERSION' },
                    [messageChannel.port2]
                );
            });
        }

        // ====================================================================
        // BACKGROUND SYNC
        // ====================================================================

        function registerBackgroundSync(tag) {
            if (!registration || !registration.sync) {
                console.log('[PWA] Background sync not supported');
                return Promise.reject('Background sync not supported');
            }

            return registration.sync.register(tag).then(function () {
                console.log('[PWA] Background sync registered:', tag);
            });
        }

        // ====================================================================
        // ONLINE/OFFLINE DETECTION
        // ====================================================================

        function handleOnline() {
            console.log('[PWA] Back online');
            service.isOnline = true;
            $timeout(function () {
                $rootScope.$broadcast('pwa:online');
            });
        }

        function handleOffline() {
            console.log('[PWA] Gone offline');
            service.isOnline = false;
            $timeout(function () {
                $rootScope.$broadcast('pwa:offline');
            });
        }

        // ====================================================================
        // SERVICE WORKER MESSAGES
        // ====================================================================

        function handleSWMessage(event) {
            console.log('[PWA] Message from SW:', event.data);

            if (event.data && event.data.type === 'CACHE_CLEARED') {
                $timeout(function () {
                    $rootScope.$broadcast('pwa:cache-cleared');
                });
            }
        }

        // ====================================================================
        // UI HELPERS
        // ====================================================================

        function hideInstallBanner() {
            var banner = document.getElementById('pwa-install-banner');
            if (banner) {
                banner.classList.remove('pwa-show');
                $timeout(function () {
                    banner.remove();
                }, 300);
            }
        }

        function showUpdateNotification() {
            // Create and show update notification with current language
            var notification = document.createElement('div');
            notification.id = 'pwa-update-notification';
            notification.className = 'pwa-update-notification';
            notification.innerHTML = `
                <div class="pwa-notification-content">
                    <div class="pwa-notification-icon">🔄</div>
                    <div class="pwa-notification-text">
                        <strong>` + getTranslation('updateReady') + `</strong>
                        <p>` + getTranslation('newVersionAvailable') + `</p>
                    </div>
                    <div class="pwa-notification-actions">
                        <button class="pwa-btn-update" onclick="angular.element(document.body).injector().get('pwaSvc').updateServiceWorker()">
                            ` + getTranslation('updateButton') + `
                        </button>
                        <button class="pwa-btn-later" onclick="this.parentElement.parentElement.parentElement.remove()">
                            ` + getTranslation('laterButton') + `
                        </button>
                    </div>
                </div>
            `;

            document.body.appendChild(notification);

            // Animate in
            $timeout(function () {
                notification.classList.add('pwa-show');
            }, 100);
        }
    }
})();
